import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Package, Check, AlertTriangle, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import {
  PackageConfirmationStatusDto,
  ShipmentPackageDto,
  ConfirmPackageRequest
} from '../../../../types/packing';
import { packingService } from '../../../../services/packingService';
import { useTranslation } from '../../../../contexts/I18nProvider';

interface PackageConfirmationSectionProps {
  groupingId: string;
  shipmentId: number;
  onAllPackagesConfirmed?: () => void;
  /** If true, package confirmation is required before finalization */
  isRequired?: boolean;
}

/**
 * PackageConfirmationSection - Section for confirming packages in a shipment.
 * Supports scanning packages in any order (non-sequential).
 * Shows packages from both batch picks (Packages table) and standalone picks (PListOrderPackages).
 */
export const PackageConfirmationSection: React.FC<PackageConfirmationSectionProps> = ({
  groupingId,
  shipmentId,
  onAllPackagesConfirmed,
  isRequired = false
}) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<PackageConfirmationStatusDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scanValue, setScanValue] = useState('');
  const [scanError, setScanError] = useState<string | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [confirmedInSession, setConfirmedInSession] = useState<Set<number>>(new Set());

  const inputRef = useRef<HTMLInputElement>(null);

  // Load package confirmation status
  const loadStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await packingService.getPackageConfirmationStatus(groupingId);
      setStatus(data);

      // Check if all confirmed
      if (data.allConfirmed && onAllPackagesConfirmed) {
        onAllPackagesConfirmed();
      }
    } catch (err) {
      console.error('Failed to load package confirmation status:', err);
      setError(t('warehouse.modules.packing.errors.loadPackageListFailed'));
    } finally {
      setLoading(false);
    }
  }, [groupingId, onAllPackagesConfirmed]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  // Focus input when section is expanded
  useEffect(() => {
    if (isExpanded && inputRef.current && !loading) {
      inputRef.current.focus();
    }
  }, [isExpanded, loading]);

  // Handle scan input
  const handleScanInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setScanValue(e.target.value);
    setScanError(null);
  };

  // Check if a package is confirmed (either from database or in this session)
  const isPackageConfirmed = (pkg: ShipmentPackageDto): boolean => {
    return pkg.isConfirmed || confirmedInSession.has(pkg.packageId);
  };

  // Handle confirm (Enter key or button) - allows scanning in any order
  const handleConfirm = async () => {
    if (!scanValue.trim() || !status) return;

    const scannedId = parseInt(scanValue.trim(), 10);
    if (isNaN(scannedId)) {
      setScanError(t('warehouse.modules.packing.errors.invalidPackageNumber'));
      setScanValue('');
      return;
    }

    // Find the scanned package in the list (allow any order)
    const scannedPackage = status.packages.find(p => p.packageId === scannedId);
    if (!scannedPackage) {
      setScanError(t('warehouse.modules.packing.errors.packageNotInShipment', { '0': scannedId.toString() }));
      setScanValue('');
      return;
    }

    // Check if already confirmed
    if (isPackageConfirmed(scannedPackage)) {
      setScanError(t('warehouse.modules.packing.errors.packageAlreadyConfirmed', { '0': scannedId.toString() }));
      setScanValue('');
      return;
    }

    setIsConfirming(true);
    setScanError(null);

    try {
      const request: ConfirmPackageRequest = {
        shipmentID: shipmentId,
        scannedPackageId: scannedId,
        expectedPackageId: scannedPackage.packageId,
        sourceType: scannedPackage.sourceType,
        pListID: scannedPackage.pListID,
        orderNo: scannedPackage.orderNo ?? undefined
      };

      const response = await packingService.confirmPackage(request);

      if (response.success) {
        // Add to session confirmed set (for batch packages that don't persist to DB)
        setConfirmedInSession(prev => new Set(prev).add(scannedId));

        // Clear input
        setScanValue('');

        // Check if all confirmed after this one
        const remainingUnconfirmed = status.packages.filter(
          p => !isPackageConfirmed(p) && p.packageId !== scannedId
        );

        if (remainingUnconfirmed.length === 0 && onAllPackagesConfirmed) {
          onAllPackagesConfirmed();
        }

        // Reload status to get updated state from server
        await loadStatus();
      } else {
        setScanError(response.message || t('warehouse.modules.packing.errors.confirmPackageFailed'));
      }
    } catch (err) {
      console.error('Failed to confirm package:', err);
      setScanError(t('warehouse.modules.packing.errors.confirmPackageFailed'));
    } finally {
      setIsConfirming(false);
      inputRef.current?.focus();
    }
  };

  // Handle key down (Enter to confirm)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleConfirm();
    }
  };

  // Calculate confirmed count including session confirmations
  const getConfirmedCount = (): number => {
    if (!status) return 0;
    return status.packages.filter(p => isPackageConfirmed(p)).length;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-center gap-2 text-gray-500">
          <Loader2 className="animate-spin" size={20} />
          <span>{t('warehouse.modules.packing.loadingPackageList')}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 rounded-lg border border-red-200 p-4">
        <div className="flex items-center gap-2 text-red-700">
          <AlertTriangle size={20} />
          <span>{error}</span>
          <button
            onClick={loadStatus}
            className="ml-auto text-sm underline hover:no-underline"
          >
            {t('common.buttons.tryAgain')}
          </button>
        </div>
      </div>
    );
  }

  if (!status || status.totalPackages === 0) {
    return (
      <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
        <div className="flex items-center gap-2 text-gray-500">
          <Package size={20} />
          <span>{t('warehouse.modules.packing.noPackagesToConfirm')}</span>
        </div>
      </div>
    );
  }

  const confirmedCount = getConfirmedCount();
  const allConfirmed = confirmedCount >= status.totalPackages;
  const remainingCount = status.totalPackages - confirmedCount;
  const progressPercent = Math.round((confirmedCount / status.totalPackages) * 100);

  return (
    <div className={`bg-white rounded-lg border ${allConfirmed ? 'border-green-300' : 'border-orange-300'} overflow-hidden`}>
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`w-full flex items-center justify-between p-4 ${allConfirmed ? 'bg-green-50' : 'bg-orange-50'} hover:bg-opacity-80 transition-colors`}
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${allConfirmed ? 'bg-green-100' : 'bg-orange-100'}`}>
            <Package className={allConfirmed ? 'text-green-600' : 'text-orange-600'} size={24} />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-gray-900">
              {t('warehouse.modules.packing.packageConfirmation')} {isRequired && <span className="text-red-500">*</span>}
            </h3>
            <p className="text-sm text-gray-600">
              {allConfirmed ? (
                <span className="text-green-600 font-medium">{t('warehouse.modules.packing.allPackagesConfirmed')}</span>
              ) : (
                <span>{t('warehouse.modules.packing.packagesConfirmedStatus', { '0': confirmedCount.toString(), '1': status.totalPackages.toString() })}</span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Progress badge */}
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
            allConfirmed
              ? 'bg-green-100 text-green-800'
              : 'bg-orange-100 text-orange-800'
          }`}>
            {progressPercent}%
          </span>
          {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
      </button>

      {/* Content */}
      {isExpanded && (
        <div className="p-4 space-y-4">
          {/* Remaining packages info */}
          {!allConfirmed && (
            <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
              <p className="text-sm text-blue-600 font-medium mb-1">{t('warehouse.modules.packing.scanInAnyOrder')}</p>
              <p className="text-2xl font-bold text-blue-800">
                {remainingCount} {remainingCount === 1 ? t('warehouse.modules.packing.packageRemaining') : t('warehouse.modules.packing.packagesRemaining')}
              </p>
            </div>
          )}

          {/* Scan input */}
          {!allConfirmed && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                {t('warehouse.modules.packing.scanPackageBarcode')}:
              </label>
              <div className="flex gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={scanValue}
                  onChange={handleScanInput}
                  onKeyDown={handleKeyDown}
                  disabled={isConfirming}
                  className={`flex-1 px-4 py-3 text-lg border-2 rounded-lg focus:outline-none transition-colors ${
                    scanError
                      ? 'border-red-500 bg-red-50 focus:border-red-600'
                      : 'border-gray-300 focus:border-blue-500'
                  }`}
                  placeholder={t('warehouse.modules.packing.scanOrEnterPackageNumber')}
                  autoComplete="off"
                />
                <button
                  onClick={handleConfirm}
                  disabled={isConfirming || !scanValue.trim()}
                  className={`px-6 py-3 rounded-lg font-medium flex items-center gap-2 transition-colors ${
                    isConfirming || !scanValue.trim()
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {isConfirming ? (
                    <Loader2 className="animate-spin" size={20} />
                  ) : (
                    <Check size={20} />
                  )}
                  {t('common.buttons.confirm')}
                </button>
              </div>
              {scanError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
                  <AlertTriangle className="text-red-600 flex-shrink-0" size={20} />
                  <p className="text-sm text-red-800 font-medium">{scanError}</p>
                </div>
              )}
            </div>
          )}

          {/* Package list */}
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <Package size={16} />
              {t('warehouse.modules.packing.allPackages', { '0': status.totalPackages.toString() })}
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
              {status.packages.map((pkg) => {
                const confirmed = isPackageConfirmed(pkg);
                return (
                  <div
                    key={`${pkg.sourceType}-${pkg.packageId}`}
                    className={`p-3 rounded-lg border-2 text-center transition-all ${
                      confirmed
                        ? 'border-green-300 bg-green-50'
                        : 'border-gray-200 bg-gray-50 hover:border-blue-300 hover:bg-blue-50'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-1">
                      {confirmed && <Check className="text-green-600" size={16} />}
                      <span className={`text-lg font-bold ${
                        confirmed ? 'text-green-700' : 'text-gray-700'
                      }`}>
                        {pkg.visiblePackageNo}
                      </span>
                    </div>
                    <div className={`text-xs ${confirmed ? 'text-green-600' : 'text-gray-400'}`}>
                      ID: {pkg.packageId}
                    </div>
                    <span className={`text-xs ${
                      pkg.sourceType === 'Batch' ? 'text-purple-600' : 'text-gray-500'
                    }`}>
                      {pkg.sourceType === 'Batch' ? t('warehouse.modules.packing.batch') : t('warehouse.modules.packing.orderWithNumber', { '0': pkg.orderNo?.toString() || '' })}
                    </span>
                    {pkg.packZoneID && (
                      <div className="text-xs text-indigo-600 font-medium mt-0.5">
                        {pkg.packZoneID}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PackageConfirmationSection;
